import React, { useEffect, useState, useMemo, useCallback } from 'react'
import Box from '@mui/material/Box'
import { getObjectsWithFilter, getObject, editObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import OrderPDF from './OrderPDF'
import { buildOrderPayload, convertDocumentAndUploadFile } from '../../../utils/documentManager'
import { getOrderFields } from './OrderFields'
import useSaveEntity from '../../hooks/useSaveEntity'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import Toolbar from '../../toolbar/Toolbar'

const ROUTES = {
  EDIT: '/orders/edit',
  VIEW: '/orders/view',
  ADD: '/orders/add'
}

const EditOrder = () => {
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const path = location.pathname
  const orderId = location.state?.id

  const [order, setOrder] = useState({ lines: [] })
  const [company, setCompany] = useState({})
  const [img, setImg] = useState('')
  const [settings, setSettings] = useState({})
  const [isSubmit, setIsSubmit] = useState(false)

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouvelle Commande' : `Commande ${order.order_number}`
      }
    }),
    [order, path]
  )

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const setLines = useCallback((newLines) => setOrder((prev) => ({ ...prev, lines: newLines })), [])

  const orderQuery = mode === 'edit' ? { key: 'order', fn: getObject, args: ['orders', orderId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    orderQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'orders',
    mode,
    id: orderId,
    user,
    settingsKey: 'order_number',
    counterName: 'orders_nb',
    updateCounter: true,
    editFn: editObject,
    contractLineName: 'Orders',
    args: [order],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setOrder(res.data)
      convertDocumentAndUploadFile(
        <OrderPDF order_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `commande_${res.data.order_number}`,
        user,
        'order',
        'orders',
        res.data
      )
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildOrderPayload
  })

  const memoizedFields = useMemo(() => getOrderFields(user, location, setLines), [user, location, setLines])

  const options = useMemo(() => ({ fields: memoizedFields, data: order }), [memoizedFields, order])

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.settings) setSettings(fetched.settings)
      if (fetched.company) {
        setCompany(fetched.company)
        const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
        if (attach?.file?.data) {
          const blob = new Blob([new Uint8Array(attach.file.data)])
          const reader = new FileReader()
          reader.onloadend = () => {
            const base64 = reader.result?.toString().split(',')[1] ?? ''
            setImg(base64)
          }
          reader.readAsDataURL(blob)
        }
      }
      if (fetched.order) setOrder(fetched.order)
    }
  }, [fetched, loadingEntities])

  const totalAmount = useMemo(() => {
    return (order.lines || []).reduce((sum, line) => {
      const qty = parseFloat(line.quantity) || 0
      const price = parseFloat(line.unit_price) || 0
      return sum + qty * price
    }, 0)
  }, [order.lines])

  useEffect(() => {
    setOrder((prev) => ({ ...prev, total_amount_net: totalAmount }))
  }, [totalAmount])

  useEffect(() => {
    if (isSubmit) {
      const cleanedOrder = {
        ...order,
        lines: (order.lines || []).map((line) => ({
          ...line,
          priceBook_id: line.priceBook_id < -1 ? -1 : line.priceBook_id
        }))
      }
      saveEntity({ data: cleanedOrder })
    }
  }, [isSubmit])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 65px)', overflowY: 'auto', flex: 1 }}>
      <Toolbar options={toolbarOptions} object={order} />

      <Form options={options} mode='edit' setDataParent={setOrder} setIsSubmitParent={setIsSubmit} loading={loading} />
    </Box>
  )
}

export default EditOrder
